import { useEffect, useRef, useState } from "react";

/** Porté à l'identique depuis apps/web/components/layout/CartCountBadge.tsx ("use client" déjà, aucune dépendance Next.js dans l'original). */
export type CartCountBadgeTone = "gold" | "navy" | "cream";

interface CartCountBadgeProps {
  count: number;
  /** Couleurs au repos ; l'état "pulse" (ajout détecté) a sa propre couleur, fixe, quel que soit le ton. */
  tone?: CartCountBadgeTone;
  /** Classes de positionnement/taille propres à l'emplacement — jamais de couleur, gérée ici. */
  className?: string;
  /**
   * `true` (défaut) : pastille absente à 0, pour les superpositions discrètes
   * (icône panier mobile, barre d'onglets). `false` : toujours affichée, y
   * compris "0" — pour un usage en texte inline (ex. "Panier (0)").
   */
  hideWhenZero?: boolean;
}

const REST_TONE_CLASSES: Record<CartCountBadgeTone, string> = {
  gold: "bg-gold text-navy",
  navy: "bg-navy text-goldl",
  cream: "bg-cream/20 text-cream",
};

/**
 * Pastille du compteur panier. Ici `count` vient d'un hook TanStack Query
 * (`useCartItemCount()`, cf. src/lib/cart.ts) plutôt que d'un rendu serveur —
 * cette pastille compare toujours le nouveau `count` à l'ancien et, s'il a
 * augmenté, joue une brève animation avant de revenir à son style habituel.
 */
export function CartCountBadge({ count, tone = "gold", className = "", hideWhenZero = true }: CartCountBadgeProps) {
  const previousCount = useRef(count);
  const [isPulsing, setIsPulsing] = useState(false);

  useEffect(() => {
    if (count > previousCount.current) {
      setIsPulsing(true);
      const timer = setTimeout(() => setIsPulsing(false), 700);
      previousCount.current = count;
      return () => clearTimeout(timer);
    }
    previousCount.current = count;
    return undefined;
  }, [count]);

  if (count <= 0 && hideWhenZero) return null;

  return (
    <span
      aria-hidden="true"
      className={[
        "inline-flex items-center justify-center rounded-full font-semibold transition-all duration-300",
        isPulsing ? "scale-125 bg-emerald-500 text-white shadow-md" : REST_TONE_CLASSES[tone],
        className,
      ].join(" ")}
    >
      {count}
    </span>
  );
}
